import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Form } from 'antd';
import { FormComponentProps } from 'antd/es/form/Form';
import { MobXProviderContext, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import useAppStores from '@app/hooks/useAppStores';
import useForm from '@app/hooks/useForm';
import useMountEffect from '@app/hooks/useMountEffect';
import useTheme from '@app/hooks/useTheme';
import { VALIDATION_PATTERNS } from '@app/domain/uiConsts';
import { GetFieldDecoratorOptionsExtended } from '@app/utils/form/form';
import { convertToTranslatedMessage } from '@app/utils/form/formTranslatedMessage';
import { emailAdditionalValidations } from '@app/utils/validators';
import { trim } from '@app/utils/stringUtils';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import TransitionLoader from '@app/components/TransitionLoader';
import { GuestLoginBrandedTheme, GuestLoginI18nNamespaceProvider } from '@app/guestLogin/domain/guestLogin';
import GuestLoginEnterEmailCode from '@app/guestLogin/routes/email/GuestLoginEnterEmailCode';
import SupplierRegistrationFormItemBox from '@supplierRegistration/components/SupplierRegistrationFormItemBox';
import ReferringOrg from '@supplierRegistration/components/ReferringOrg';
import { FIELD_MAX_LENGTH } from '@supplierRegistration/domain/supplierRegistrationFields';
import { SupplierValidationRegistrationProps } from '@supplierRegistration/domain/supplierRegistration';
import {
  guestSession,
  SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE,
} from '@supplierRegistration/services/supplierRegistrationGuestSessionService';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import { CardSubTitle, CardTitle, MainCard, SmallContinueButton, StyledForm } from '@supplierRegistration/routes/Styles';

interface EmailVerificationFormFields {
  email: string;
}

interface Props extends FormComponentProps<EmailVerificationFormFields>, SupplierValidationRegistrationProps {}

// Mirrors the email field's own validation rules (required / max length / type / local-part
// length -- see emailFieldDecorator and emailAdditionalValidations below). An async-prefilled
// email must pass this before it is trusted enough to render locked and force-enable Continue --
// otherwise a blank or malformed prefill would lock the field with nothing the supplier could fix
// and enable Continue against a value the knoxer will just reject.
const MAX_EMAIL_LOCAL_PART = 64;

function isValidEmailFormat(email: string | null | undefined): email is string {
  const trimmed = email?.trim();

  if (!trimmed || trimmed.length > FIELD_MAX_LENGTH.email || !VALIDATION_PATTERNS.email.test(trimmed)) {
    return false;
  }

  const [localPart] = trimmed.split('@');
  return (localPart?.length ?? 0) <= MAX_EMAIL_LOCAL_PART;
}

const SupplierRegistrationEmailVerification: FC<Props> = observer((props) => {
  const { configuration, nextStep, form } = props;
  const { t } = useTranslation();
  const theme = useTheme<SupplierRegistrationMode>();
  const { brand, continueButtonColor, fontFamily } = theme;
  const { supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();
  // Validation runs on every change so Continue enables as soon as the address is valid, but the
  // message itself waits until the supplier leaves the field or tries to continue.
  const { isFormInvalid, showFormErrors, setShowFormErrors } = useForm(props, { initialShowErrors: 'none' });
  const revealFormErrors = (): void => setShowFormErrors('all');

  const [isWaitingForCode, setIsWaitingForCode] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState<string | undefined>(undefined);
  const [requestCodeErrorText, setRequestCodeErrorText] = useState<string | null>(null);

  const asyncProcessMaskedData = supplierRegistrationCustomerDataStore.currentAsyncProcessMaskedData?.result;
  const prefilledEmail = asyncProcessMaskedData?.contactInfo.email;
  const hasAsyncPrefilledEmail = isValidEmailFormat(prefilledEmail);
  // The field unmounts entirely while the code screen is showing, which drops its antd Form
  // state -- restoring the last attempted (already-valid, since Continue only enables for one)
  // email as the initialValue on the way back mirrors GuestLoginEmailFlow's own
  // initialEmail={email} handoff into GuestLoginEnterEmail.
  const initialEmailValue = hasAsyncPrefilledEmail ? prefilledEmail : verifyingEmail;

  // Restores an already-verified session from storage as soon as this step exists, mirroring
  // EmailVerificationModal's own mount effect. SupplierRegistrationFormPage already kicks this off
  // on its own mount (so a returning supplier's welcome click can skip this step entirely) -- this
  // is only a safety net for whatever path lands here before that has resolved.
  useMountEffect(() => {
    void guestSession.init();
  });

  const { isVerified, verifiedEmail } = guestSession;

  // Handles both: a session that resolves as verified while this step is already showing, and
  // landing on this step in the first place with a stale/unresolved verification check (the
  // welcome-click race init() is meant to avoid, but this covers it either way).
  useEffect(() => {
    if (isVerified && verifiedEmail) {
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified, verifiedEmail]);

  const emailFieldDecorator: GetFieldDecoratorOptionsExtended = {
    initialValue: initialEmailValue ?? undefined,
    disableAllRules: hasAsyncPrefilledEmail,
    rules: [
      {
        required: true,
        transform: trim,
        translatedMessage: 'supplierValidation.supplierRegister.errors.invalidEmail',
      },
      {
        max: FIELD_MAX_LENGTH.email,
        transform: trim,
        translatedMessage: {
          key: 'supplierValidation.supplierRegister.maxCharacters',
          value: FIELD_MAX_LENGTH.email,
        },
      },
      {
        type: 'email',
        transform: trim,
        translatedMessage: 'supplierValidation.supplierRegister.errors.invalidEmail',
      },
      {
        validator: emailAdditionalValidations(
          convertToTranslatedMessage('supplierValidation.supplierRegister.errors.invalidEmail'),
        ),
      },
    ],
  };

  const handleOk = (): void => {
    revealFormErrors();

    form.validateFieldsAndScroll(async (errors: Object, values: EmailVerificationFormFields) => {
      if (errors) {
        return;
      }

      const email = values.email.trim();
      const didRequestSucceed = await guestSession.requestCodeFromEmailKnoxer(email);

      if (didRequestSucceed) {
        setRequestCodeErrorText(null);
        setVerifyingEmail(email);
        setIsWaitingForCode(true);
      } else {
        // Never a silent no-op -- surfaced the same way GuestLoginEnterEmailCode surfaces its own
        // code-field errors: inline, under the field, via forcedErrorText.
        setRequestCodeErrorText(t('supplierValidation.registerSteps.emailVerification.errors.requestCodeFailed'));
      }
    });
  };

  const handleEmailInputInteraction = (): void => {
    if (requestCodeErrorText) {
      setRequestCodeErrorText(null);
    }
  };

  if (verifyingEmail && isWaitingForCode) {
    // GuestLoginBrandedTheme expects brand.clientName; supplierRegistration's theme carries
    // brand.customerName instead -- map it explicitly or the guestLogin screens misread the brand.
    const guestTheme: GuestLoginBrandedTheme = {
      ...theme,
      brand: brand ? { ...brand, clientName: brand.customerName } : undefined,
    };

    return (
      <MainCard fontFamily={fontFamily}>
        <MobXProviderContext.Provider
          value={{ infraStores: { guestLoginStore: guestSession, themeStore: { theme: guestTheme } } }}
        >
          <GuestLoginI18nNamespaceProvider value={SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE}>
            <TransitionLoader loading={guestSession.isLoginLoading}>
              <GuestLoginEnterEmailCode email={verifyingEmail} cancelWaitingForCode={(): void => setIsWaitingForCode(false)} />
            </TransitionLoader>
          </GuestLoginI18nNamespaceProvider>
        </MobXProviderContext.Provider>
      </MainCard>
    );
  }

  return (
    <MainCard fontFamily={fontFamily}>
      {configuration?.clientName && !brand && <ReferringOrg orgName={configuration.clientName} />}
      <StyledForm form={form} showErrors={showFormErrors} setShowErrors={setShowFormErrors}>
        <EmailVerificationTitle>{t('supplierValidation.registerSteps.emailVerification.title')}</EmailVerificationTitle>
        <CardSubTitle>{t('supplierValidation.registerSteps.emailVerification.subtitle')}</CardSubTitle>
        <SupplierRegistrationFormItemBox
          fieldName='email'
          fieldDecoratorOptions={emailFieldDecorator}
          hasPrefilledData={hasAsyncPrefilledEmail}
          forcedErrorText={requestCodeErrorText}
          onBlur={revealFormErrors}
        >
          <NakedFormInput
            name='inpt-supp-reg-email-verification-email'
            dataTestId='inpt-supp-reg-email-verification-email'
            type='text'
            placeholder={t<string>('supplierValidation.registerSteps.emailVerification.emailLabel')}
            onClick={handleEmailInputInteraction}
            onFocus={handleEmailInputInteraction}
            disableSuggestion
          />
        </SupplierRegistrationFormItemBox>
        <SmallContinueButton
          id='btn-supp-reg-email-verification-continue'
          // An async-prefilled email has disableAllRules set on its only field, so it never runs
          // any validation -- useForm's isFormInvalid reads that as "nothing validated yet" and
          // would keep Continue disabled forever. The prefilled value is already trusted (it came
          // from the payer's invite), so it can enable Continue outright.
          disabled={hasAsyncPrefilledEmail ? false : isFormInvalid}
          loading={guestSession.isLoginLoading}
          onClick={handleOk}
          colorScheme={continueButtonColor}
        >
          {t('supplierValidation.registerSteps.emailVerification.continue')}
        </SmallContinueButton>
      </StyledForm>
    </MainCard>
  );
});

export default Form.create<Props>()(SupplierRegistrationEmailVerification);

const EmailVerificationTitle = styled(CardTitle)`
  font-size: 32px;
  font-weight: 600;
`;
